import Close from '@mui/icons-material/Close'
import Edit from '@mui/icons-material/Edit'
import Send from '@mui/icons-material/Send'
import { Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Fab } from '@mui/material'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmationModal from 'src/consumer/components/helperComponents'
import { TOAST_UTILS, UNIQUE_COLORS_100 } from 'src/consumer/constants'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import {
  defaultConfirmationModalProps,
  generateUniqueHexWithOpacity,
} from 'src/consumer/helpers/utilFunctions'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import { getMPDataWithParams } from 'src/mentor/services'
import { getAllOneOnOnePlans } from '../MentorEventsPage/MentorEventsHelpers'
import MentorHeader from '../MentorHeader'
import ActivityPlanValidationComponent from './components/ActivityPlanValidationComponent'
import CustomPlansComponent from './components/CustomPlansComponent'
import { StyledCancelEditFAB, StyledMainHeadingCustomPlan } from './components/styledComponents'
import {
  ACTIVITY_PLAN_OBJECT,
  fixDaysAndContent,
  fixStartDaysAndEndDays,
  formSavableActivityPlan,
  getSingleActivityPlan,
  getSubscriptionLevels,
  saveActivtyPlan,
  validateActivityPlan,
} from './utils'

export default function CustomPlansForm() {
  const { allTags } = useContext(MentorSetupContext)
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const { mentorProfileData, allLegalPolicies } = useContext(MentorProfileContext)
  const unAcceptedAgreements = allLegalPolicies?.filter((agreement) => !agreement?.signed)?.length

  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const mode = searchParams.get('mode')
  const disabledFields = mode === 'View'
  const [loading, setLoading] = useState(true)

  const [customPlan, setCustomPlan] = useState(ACTIVITY_PLAN_OBJECT)
  const [initialCustomPlan, setInitialCustomPlan] = useState(ACTIVITY_PLAN_OBJECT)
  const [phaseContent, setPhaseContent] = useState()
  const changePhase = useCallback(
    (prevPhase, idx) => {
      setCustomPlan((prev) => {
        const isPrevFunc = typeof prevPhase === 'function'
        let toSet = fixStartDaysAndEndDays(
          prev?.phases.map((phs, phsIndex) => {
            if (phsIndex === idx) {
              return isPrevFunc ? prevPhase(phs) : prevPhase
            }
            return phs
          }),
        )
        setPhaseContent((prev) => fixDaysAndContent(customPlan, toSet, prev))
        return {
          ...prev,
          phases: toSet,
        }
      })
    },
    [setCustomPlan],
  )
  const [showValidationDialog, setShowValidationDialog] = useState(false)
  const [activityPlanValidation, setActivityObjectValidation] = useState({})
  const [confirmationModalProps, setConfirmationModalProps] = useState(
    defaultConfirmationModalProps,
  )
  const [saveType, setSaveType] = useState('draft')
  const store = useSelector((state) => state)
  const [dropDownData, setDropDownData] = useState({})

  const handleChange = useCallback(
    (event) => {
      setCustomPlan((prev) => ({ ...prev, [event.target.id]: event.target.value }))
    },
    [setCustomPlan],
  )

  async function GetSubscriptionOptions() {
    const levels = await getSubscriptionLevels()
    setDropDownData((prev) => ({ ...prev, subscriptionLevels: levels }))
  }

  async function get1on1Plans() {
    const plans = await getAllOneOnOnePlans()
    setDropDownData((prev) => ({ ...prev, oneOnOnePlans: plans }))
  }
  async function getMetaCategories() {
    const resp = await getMPDataWithParams('/api/secure/teacher/meta-categories', {
      enabledForDailyPlay: true,
    })
    if (resp.status === 200) {
      setDropDownData((prev) => ({ ...prev, metaCategories: resp.data }))
    }
  }

  async function Setup() {
    let customPlanToSet = { ...ACTIVITY_PLAN_OBJECT, mentorId: mentorProfileData.id }
    let promises = []
    if (mode !== 'Add' || id !== 'new-plan') {
      promises.push(getSingleActivityPlan(id, mentorProfileData.id))
    }
    promises.push(GetSubscriptionOptions())
    promises.push(get1on1Plans())
    promises.push(getMetaCategories())
    setDropDownData((prev) => ({ ...prev, languages: unSecureSetupData?.appLanguages }))
    setLoading(true)
    const allPromises = await Promise.all(promises)
    if (mode !== 'Add' || id !== 'new-plan') {
      customPlanToSet = allPromises?.[0]
      customPlanToSet = {
        ...customPlanToSet,
        plannedActivities: customPlanToSet?.plannedActivities?.map((plannedActivity) => ({
          ...plannedActivity,
          otherActivities: plannedActivity?.plannedActivity
            ? [plannedActivity?.plannedActivity, ...(plannedActivity?.otherActivities || [])]
            : [...(plannedActivity?.otherActivities || [])],
        })),
        phaseList: customPlanToSet?.phaseList?.map((phase) => ({
          ...phase,
          days: phase?.lastDay - phase?.firstDay + 1,
        })),
      }
    }
    const allPhasesFixed = fixStartDaysAndEndDays(customPlanToSet.phaseList)
    const builtCustomPlan = {
      ...customPlanToSet,
      phaseList: allPhasesFixed?.map((phase, idx) => ({
        ...phase,
        legendColor: UNIQUE_COLORS_100?.[idx] || generateUniqueHexWithOpacity(1),
      })),
    }
    setCustomPlan(builtCustomPlan)
    setInitialCustomPlan(builtCustomPlan)
    setLoading(false)
  }

  async function saveActivityPlan() {
    const loadingTst = toast.loading('Saving Activity Plan...')

    let response
    let idToUse = customPlan?.id
    let activityPlanToUse = customPlan

    // if (mode === 'Add' || idToUse === '' || idToUse === undefined) {
    //   const {
    //     plannedActivities,
    //     plannedActivityIds,
    //     phaseIds,
    //     phaseList,
    //     ...activityPlanWithoutData
    //   } = activityPlanToUse
    //   const resp = await saveActivtyPlan(activityPlanWithoutData)
    //   if (resp.status === 200) {
    //     idToUse = resp.data.id
    //     activityPlanToUse = { ...customPlan, ...resp.data }
    //   }
    // }

    const cleanedActivityPlan = await formSavableActivityPlan(
      activityPlanToUse,
      mentorProfileData.id,
      loadingTst,
    )
    response = await saveActivtyPlan({ ...cleanedActivityPlan, status: saveType }, loadingTst)
    if (response.status === 200) {
      toast.update(loadingTst, {
        render: 'Saved Successfully.',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        ...TOAST_UTILS,
      })
      navigate(`/mentor-home/homework-plans/${idToUse}?mode=View`)
      if (id === idToUse) {
        Setup()
      }
    } else {
      toast.update(loadingTst, {
        render: 'Something went wrong.',
        type: toast.TYPE.ERROR,
        isLoading: false,
        ...TOAST_UTILS,
      })
    }
  }

  function closeValidationDialog() {
    setShowValidationDialog(false)
  }

  function SaveModal() {
    setConfirmationModalProps((prev) => ({
      ...prev,
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
      action: 'save',
      body:
        customPlan?.plannedActivities?.length > customPlan?.totalDays ? (
          <>
            <span className="text-warning">
              <strong>
                There are some planned activities that exceed the total number of days. These will
                be removed and not saved.
              </strong>
            </span>
            <span>Are you sure you want to save this activity plan?</span>
          </>
        ) : (
          'Are you sure you want to save this activity plan?'
        ),
      buttonColor: 'success',
      onSubmitFunctions: [
        async () => {
          await saveActivityPlan()
        },
      ],
    }))
  }

  async function handleSubmit(event) {
    const form = event.currentTarget
    if (form.checkValidity() === false) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      event.preventDefault()
      const validatedActivityPlan = validateActivityPlan(customPlan)
      setActivityObjectValidation(validatedActivityPlan)
      if (
        !!validatedActivityPlan?.title?.valid &&
        !!validatedActivityPlan?.phases?.valid &&
        validatedActivityPlan.plannedActivities.every((pActivity) => pActivity.valid)
      ) {
        SaveModal()
        // console.log({
        //   plannedActivities: customPlan?.plannedActivities,
        //   totalDays: customPlan?.totalDays,
        //   comparison: customPlan?.plannedActivities?.length >= customPlan?.totalDays,
        // })
      } else {
        setShowValidationDialog(true)
      }
    }
  }

  function moveToEdit() {
    navigate(`/mentor-home/homework-plans/${id}?mode=Edit`)
  }

  function moveToView() {
    navigate(`/mentor-home/homework-plans/${id}?mode=View`)
  }
  function cancelEditing() {
    moveToView()
    setCustomPlan(initialCustomPlan)
  }

  function handleCancel() {
    const planChanged = JSON.stringify(customPlan) !== JSON.stringify(initialCustomPlan)
    if (planChanged) {
      setConfirmationModalProps((prev) => ({
        ...prev,
        visibility: true,
        visibilitySetter: setConfirmationModalProps,
        action: 'move to view',
        body: 'Executing this action will restore the plan to its original state, erasing any changes made. Are you absolutely sure you want to proceed?',
        buttonColor: 'secondary',
        onSubmitFunctions: [cancelEditing],
      }))
    } else {
      cancelEditing()
    }
  }

  useEffect(() => {
    Setup()
  }, [id])

  //TODO: DELAY THE TITLE CHANGE ON THE HEADING
  return (
    <>
      <MentorHeader heading="Plans and Homework" agreements={unAcceptedAgreements} />
      <div
        style={{
          maxWidth: '1000px',
          width: '100%',
          margin: '0 auto',
          flexGrow: 1,
          padding: '0px 0px 30px 0px',
        }}
      >
        {loading ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress sx={{ color: '#a966ff' }} />
          </div>
        ) : (
          <form className="d-flex flex-column flex-grow-1 px-4 mt-2" onSubmit={handleSubmit}>
            <div className="d-flex justify-content-between align-items-center">
              <StyledMainHeadingCustomPlan className="w-100">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Activity Plan</span>
                  {mode !== 'Add' ? (
                    <>
                      {customPlan?.status === 'draft' ? (
                        <Chip label="Draft" color="warning" />
                      ) : (
                        <Chip label="Published" color="success" />
                      )}
                    </>
                  ) : (
                    <Chip label="New" color="info" />
                  )}
                </div>
                <div style={{ fontSize: '28px' }}>
                  <span>{!!customPlan.title ? customPlan.title : <em>No Title</em>}</span>
                </div>
              </StyledMainHeadingCustomPlan>
            </div>
            <hr style={{ marginBottom: '0px' }}></hr>
            <CustomPlansComponent
              customPlan={customPlan}
              handleChange={handleChange}
              setCustomPlan={setCustomPlan}
              disabled={disabledFields}
              validationObject={activityPlanValidation}
              dropDownOptions={dropDownData}
            />
            <div
              style={{
                position: 'sticky',
                width: '100%',
                bottom: '30px',
                pointerEvents: 'none',
                padding: '0px 0px 15px 0px',
                zIndex: 2,
              }}
              className="d-flex justify-content-end align-items-center flex-wrap gap-2"
            >
              {mode !== 'View' && mode !== 'Add' && (
                <StyledCancelEditFAB
                  key={'cancel'}
                  onClick={handleCancel}
                  color="secondary"
                  sx={ACTIVITY_PLAN_FAB_SX}
                >
                  <Close />
                </StyledCancelEditFAB>
              )}
              {mode === 'View' && (
                <Fab key={'edit'} onClick={moveToEdit} color="warning" sx={ACTIVITY_PLAN_FAB_SX}>
                  <Edit />
                </Fab>
              )}
              {mode !== 'View' && (
                <Fab
                  key={'Save_draft'}
                  color={'warning'}
                  onClick={() => {
                    setSaveType('draft')
                    SaveModal()
                  }}
                  variant={'extended'}
                  sx={ACTIVITY_PLAN_FAB_SX}
                >
                  <Send />
                  Save as Draft
                </Fab>
              )}
              {mode !== 'View' && (
                <Fab
                  sx={ACTIVITY_PLAN_FAB_SX}
                  key={'Save_published'}
                  color="success"
                  type="submit"
                  variant="extended"
                  onClick={() => {
                    setSaveType('published')
                  }}
                >
                  <Send />
                  Save as Published
                </Fab>
              )}
            </div>
            <Dialog open={showValidationDialog} onClose={closeValidationDialog}>
              <DialogTitle>There are some errors in this Activity Plan</DialogTitle>
              <DialogContent>
                <ActivityPlanValidationComponent validationData={activityPlanValidation} />
              </DialogContent>
            </Dialog>
            <ConfirmationModal newImplementation {...confirmationModalProps} />
          </form>
        )}
      </div>
    </>
  )
}
const ACTIVITY_PLAN_FAB_SX = {
  pointerEvents: 'all !important',
}
